import dayjs from "dayjs";
import { EarlyBirdConditionWithUiId } from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import type { BuilderPageLoadedData } from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import { ProductSelectMode, StudyDateRangeMode } from "app/shared/model/EarlyBirdOfferDto";
import { parseProductId } from "app/pages/EarlyBirdOfferBuilder/productIdHelpers";

const fmtDate = (s: string | null | undefined): string =>
    s ? dayjs(s).format("DD-MMM-YYYY") : "-";

function notNil<T>(value: T | null | undefined): value is T {
    return value != null;
}

export const FIELD_LIBRARY_ITEMS = [
    { id: "admissionRegion", label: "Admission Region", description: "Admission Region" },
    { id: "agent", label: "Agent", description: "Specify agent and optional agent offices" },
    { id: "age", label: "Age", description: "Age on Arrival, Date of Birth (DOB)" },
    { id: "arrivalDepartureDates", label: "Booking dates", description: "Booking arrival & departure (checkout) dates" },
    { id: "bookingSystem", label: "Booking System", description: "Booking system" },
    { id: "course", label: "Product Type : Course", description: "Select course products that will receive the discount." },
    { id: "accommodation", label: "Product Type : Accommodation", description: "Select accommodation products that will receive the discount." },
    { id: "addOn", label: "Product Type : AddOn", description: "Select addons products that will receive the discount." },
    { id: "transfer", label: "Product Type : Transfer", description: "Select transfer products that will receive the discount." },
    { id: "saleCountry", label: "Sale Country", description: "Sale country" },
    { id: "school", label: "School", description: "Specify School" },
    { id: "studyStartEndDates", label: "Course Start / End dates", description: "Course start date, course end date" },
] as const;

export type FieldLibraryItemId = (typeof FIELD_LIBRARY_ITEMS)[number]["id"];

export function isFieldLibraryItemId(value: string): value is FieldLibraryItemId {
    return FIELD_LIBRARY_ITEMS.some((f) => f.id === value);
}

function defaultCondition(fieldId: FieldLibraryItemId): EarlyBirdConditionWithUiId {
    const uiId = crypto.randomUUID();

    switch (fieldId) {
        case "admissionRegion":
            return { field: "admissionRegion", uiId };
        case "agent":
            return { field: "agent", agents: [], uiId };
        case "age":
            return { field: "age", uiId };
        case "arrivalDepartureDates":
            return { field: "arrivalDepartureDates", uiId };
        case "bookingSystem":
            return { field: "bookingSystem", uiId };
        case "course":
            return { field: "course", mode: ProductSelectMode.Any, uiId };
        case "accommodation":
            return { field: "accommodation", mode: ProductSelectMode.Any, uiId };
        case "addOn":
            return { field: "addOn", mode: ProductSelectMode.Any, uiId };
        case "transfer":
            return { field: "transfer", mode: ProductSelectMode.Any, uiId };
        case "saleCountry":
            return { field: "saleCountry", uiId };
        case "school":
            return { field: "school", uiId };
        case "studyStartEndDates":
            return { field: "studyStartEndDates", mode: StudyDateRangeMode.Covers, uiId };
    }
}

export function createDefaultCondition(fieldId: FieldLibraryItemId): EarlyBirdConditionWithUiId {
    return defaultCondition(fieldId);
}

export function getFieldLabel(fieldId: FieldLibraryItemId | string): string {
    return FIELD_LIBRARY_ITEMS.find((f) => f.id === fieldId)?.label ?? fieldId;
}

function schoolIdsToNames(ids: string[], lookups: BuilderPageLoadedData): string[] {
    return ids.map(
        (id) =>
            lookups.schools.find((s) => String(s.id) === String(id))?.name ??
            lookups.schools.find((s) => (s as any).code === id)?.name ??
            id
    );
}

export function getConditionSummary(
    condition: { field: string; [k: string]: any },
    lookups?: BuilderPageLoadedData | null
): string {
    const field = condition.field;

    if (field === "studyStartEndDates" && condition.studyStartEnd) {
        const s = condition.studyStartEnd;
        const mode =
            condition.mode === StudyDateRangeMode.Within ? "Falls within"
                : condition.mode === StudyDateRangeMode.Intersect ? "Overlaps"
                    : "Runs throughout";
        return `${mode} · ${fmtDate(s.from)} – ${fmtDate(s.to)}`;
    }

    if (field === "school" && condition.schoolIds?.length) {
        if (lookups) return schoolIdsToNames(condition.schoolIds, lookups).join(", ");
        return condition.schoolIds.join(", ");
    }

    if (field === "admissionRegion" && condition.region) {
        return condition.region;
    }

    if (field === "agent" && condition.agents?.length) {
        return condition.agents
            .map((node: any) => {
                const agent = lookups?.agents?.find((a) => String(a.id) === String(node.agentId));
                const agentName = agent?.name ?? node.agentName ?? node.agentId;

                const officeNames = Array.isArray(node.agentOfficeIds)
                    ? node.agentOfficeIds.map((officeId: string) => {
                        const office = agent?.offices?.find((o) => String(o.id) === String(officeId));
                        return office?.name ?? node.agentOfficeNames?.[officeId] ?? officeId;
                    })
                    : [];

                return officeNames.length
                    ? `${agentName}: ${officeNames.join(", ")}`
                    : agentName;
            })
            .join(" | ");
    }

    if (field === "age") {
        const parts: string[] = [];
        if (condition.minAge != null) parts.push(`Min: ${condition.minAge}`);
        if (condition.maxAge != null) parts.push(`Max: ${condition.maxAge}`);
        return parts.length ? parts.join(", ") : "-";
    }

    if (field === "arrivalDepartureDates") {
        const a = condition.arrival;
        const d = condition.departure;
        const ap = a ? `Arrival ${fmtDate(a.from)}–${fmtDate(a.to)}` : "";
        const dp = d ? `Departure ${fmtDate(d.from)}–${fmtDate(d.to)}` : "";
        return [ap, dp].filter(Boolean).join(". ") || "-";
    }

    if (field === "bookingSystem" && condition.bookingSystemId) {
        if (lookups?.bookingSystems?.length) {
            const bs = lookups.bookingSystems.find((b) => b.id === condition.bookingSystemId);
            if (bs?.name) return bs.name;
        }
        return condition.bookingSystemId;
    }

    if (field === "course") {
        if (condition.mode === ProductSelectMode.Any) return "Any";

        if (condition.mode === ProductSelectMode.Select && condition.courseIds?.length) {
            const names = lookups?.courses?.length
                ? condition.courseIds.map((sid: string) => {
                    const id = parseProductId(sid);
                    return Number.isFinite(id)
                        ? lookups.courses.find((c) => c.id === id)?.name ?? sid
                        : sid;
                })
                : condition.courseIds;

            const weekPart =
                condition.minWeeks != null || condition.maxWeeks != null
                    ? ` · Weeks: ${condition.minWeeks ?? "-"}–${condition.maxWeeks ?? "-"}`
                    : "";

            return names.join(", ") + weekPart;
        }

        const weekPart =
            condition.minWeeks != null || condition.maxWeeks != null
                ? `Weeks: ${condition.minWeeks ?? "-"}–${condition.maxWeeks ?? "-"}`
                : "";

        return weekPart || "-";
    }

    if (field === "accommodation") {
        if (condition.mode === ProductSelectMode.Any) return "Any";

        if (condition.mode === ProductSelectMode.Type && condition.kind != null) {
            const name = lookups?.accommodationTypes?.find((t) => t.id === condition.kind)?.name;
            const weekPart =
                condition.minWeeks != null || condition.maxWeeks != null
                    ? ` · Weeks: ${condition.minWeeks ?? "-"}–${condition.maxWeeks ?? "-"}`
                    : "";
            return (name ?? `Type ${condition.kind}`) + weekPart;
        }

        if (condition.mode === ProductSelectMode.Select && condition.accommodationIds?.length) {
            const names = lookups?.accommodationRooms?.length
                ? condition.accommodationIds.map((sid: string) => {
                    const id = parseProductId(sid);
                    if (!Number.isFinite(id) || !lookups) return sid;

                    const room = lookups.accommodationRooms.filter(notNil).find((r) => r.id === id);
                    return room?.name ?? sid;
                })
                : condition.accommodationIds;

            const weekPart =
                condition.minWeeks != null || condition.maxWeeks != null
                    ? ` · Weeks: ${condition.minWeeks ?? "-"}–${condition.maxWeeks ?? "-"}`
                    : "";

            return names.join(", ") + weekPart;
        }

        const weekPart =
            condition.minWeeks != null || condition.maxWeeks != null
                ? `Weeks: ${condition.minWeeks ?? "-"}–${condition.maxWeeks ?? "-"}`
                : "";

        return weekPart || "-";
    }

    if (field === "addOn") {
        if (condition.mode === ProductSelectMode.Any) return "Any";

        if (condition.mode === ProductSelectMode.Type && condition.kind != null) {
            const name = lookups?.addOnTypes?.find((t) => t.id === condition.kind)?.name;
            return name ?? `Type ${condition.kind}`;
        }

        if (condition.mode === ProductSelectMode.Select && condition.addOnIds?.length) {
            const names = lookups?.addOns?.length
                ? condition.addOnIds.map((sid: string) => {
                    const id = parseProductId(sid);
                    if (!Number.isFinite(id) || !lookups) return sid;

                    const addOn = lookups.addOns.filter(notNil).find((a) => a.id === id);
                    return addOn?.name ?? sid;
                })
                : condition.addOnIds;

            return names.join(", ");
        }

        return "-";
    }

    if (field === "transfer") {
        if (condition.mode === ProductSelectMode.Any) return "Any";

        if (condition.mode === ProductSelectMode.Select && condition.transferIds?.length) {
            const names = lookups?.transfers?.length
                ? condition.transferIds.map((sid: string) => {
                    const normalized = typeof sid === "string"
                        ? `Transfer-${sid.replace(/^(Transfer-)+/i, "")}`
                        : sid;
                    const id = parseProductId(normalized);
                    if (!Number.isFinite(id) || !lookups) return normalized;

                    const transfer = lookups.transfers.filter(notNil).find(
                        (t) => t.id === id || Number(t.id) === id
                    );
                    return transfer?.name ?? normalized;
                })
                : condition.transferIds;

            return names.join(", ");
        }

        return "-";
    }

    if (field === "saleCountry" && condition.countryCodes?.length) {
        if (lookups?.countries?.length) {
            const names = condition.countryCodes.map(
                (code: string) =>
                    lookups.countries.find(
                        (c) => (c as any).id === code || (c as any).code === code
                    )?.name ?? code
            );
            return names.join(", ");
        }
        return condition.countryCodes.join(", ");
    }

    return getFieldLabel(field);
}